// PdfChart.jsx
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import "../BenFordStyle.css";

/**
 * PdfChart
 * Renders a bar/line style chart for PDF model outputs.
 * Props:
 *  - data: array of points (each may have .row with backend fields)
 *  - dep: dependent variable name (string)
 *  - groupBy: array of group-by column names
 */
const PdfChart = ({ data = [], dep, groupBy = [] }) => {
  const chartRef = useRef(null);
  const instanceRef = useRef(null);

  const extractPdfY = (rowObj, pt) => {
    const candidates = [
      "PDF",
      "pdf",
      "PDF_VALUE",
      "pdf_value",
      "PROBABILITY_DENSITY",
      "probability_density",
      dep,
    ];
    for (const c of candidates) {
      if (!c) continue;
      const val =
        (rowObj && (rowObj[c] ?? rowObj[c.toLowerCase?.()] ?? rowObj[c.toUpperCase?.()])) ??
        (pt && (pt[c] ?? pt[c.toLowerCase?.()] ?? pt[c.toUpperCase?.()]));
      if (val !== undefined && val !== null && val !== "") {
        const cleaned = String(val).replace(/,/g, "").trim();
        const parsed = parseFloat(cleaned);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    if (typeof pt?.y === "number" && Number.isFinite(pt.y)) return pt.y;
    if (typeof pt?.y === "string") {
      const p = parseFloat(pt.y.replace(/,/g, ""));
      if (!Number.isNaN(p)) return p;
    }
    return 0;
  };

  const prepare = (raw) => {
    const useGroup = Array.isArray(groupBy) && groupBy.length > 0;
    if (!Array.isArray(raw)) return { categories: [], values: [] };

    if (useGroup) {
      const groupKey = (row) =>
        groupBy
          .map((g) => (row && row[g] !== undefined ? String(row[g]) : "N/A"))
          .join(" | ");

      const agg = new Map();
      raw.forEach((pt) => {
        const row = pt.row ?? pt;
        const key = groupKey(row);
        const y = extractPdfY(row, pt);
        const entry = agg.get(key) || { sum: 0, count: 0 };
        entry.sum += y;
        entry.count += 1;
        agg.set(key, entry);
      });

      const categories = Array.from(agg.keys());
      const values = categories.map((k) => {
        const e = agg.get(k);
        return e.count > 0 ? e.sum / e.count : 0;
      });
      return { categories, values };
    }

    // No groupBy: show sequence or vendor/category if available
    const categories = [];
    const values = [];
    raw.forEach((pt, idx) => {
      const row = pt.row ?? pt;
      const label =
        row?.Vendor_Name ?? row?.Vendor ?? row?.Project_name ?? `Row ${idx + 1}`;
      categories.push(String(label));
      values.push(extractPdfY(row, pt));
    });
    return { categories, values };
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const { categories, values } = prepare(data || []);
    const count = categories.length;

    // Show smaller window initially when many categories
    let defaultEnd = 100;
    if (count > 60) defaultEnd = 20;
    else if (count > 30) defaultEnd = 40;

    const option = {
      backgroundColor: "transparent",
      title: {
        // text: "Probability Density (PDF)",
        left: "center",
        top: 8,
        textStyle: { color: "#000", fontWeight: "bold", fontSize: 16 },
      },
      legend: {
        data: ["PDF", "PDF Curve"],
        top: 8,
        textStyle: { color: "#000" },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          if (!params || !params.length) return "";
          const p = params[0];
          const fullName = categories[p.dataIndex] ?? p.name;
          return `<div style="font-size:13px;line-height:1.5;"><strong>${fullName}</strong><br/>PDF: <b>${Number(p.value).toFixed(6)}</b></div>`;
        },
      },
      grid: { left: 70, right: 30, bottom: 110, top: 60 },
      xAxis: {
        type: "category",
        data: categories,
        name: Array.isArray(groupBy) && groupBy.length ? groupBy.join(" | ") : "Category",
        nameRotate: -90,
        axisLabel: {
          rotate: 45,
          interval: 0,
          margin: 14,
          color: "#000",
          fontSize: 11,
          fontWeight: "bold",
          formatter: (v) => (typeof v === "string" && v.length > 15 ? v.slice(0, 14) + "…" : v),
        },
        axisLine: { lineStyle: { color: "#555" } },
      },
      yAxis: {
        type: "value",
        name: dep,
        axisLabel: { color: "#000", fontSize: 12, fontWeight: "bold" },
        splitLine: { show: true, lineStyle: { color: "#eee" } },
      },
      dataZoom: [
        { type: "slider", xAxisIndex: [0], start: 0, end: defaultEnd, height: 18, bottom: 18 },
        { type: "inside", xAxisIndex: [0], start: 0, end: defaultEnd },
      ],
      series: [
        {
          type: "bar",
          name: "PDF",
          data: values,
          barWidth: Math.max(6, Math.floor(600 / Math.max(1, count))),
          itemStyle: { color: "#6366F1", opacity: 0.95 },
          emphasis: { focus: "series", itemStyle: { color: "#4F46E5" } },
          zlevel: 1,
        },
        {
          type: "line",
          name: "PDF Curve",
          data: values,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#ef4444", width: 2.5, opacity: 0.95 },
          areaStyle: { opacity: 0 },
          zlevel: 3,
        },
      ],
    };

    if (!instanceRef.current) {
      instanceRef.current = echarts.init(chartRef.current);
    }
    instanceRef.current.setOption(option, true);

    const handleResize = () => instanceRef.current && instanceRef.current.resize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data, dep, groupBy]);

  useEffect(() => {
    return () => {
      if (instanceRef.current) {
        try { instanceRef.current.dispose(); } catch {}
        instanceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className="one-wrapper-BenFord"
      ref={chartRef}
      style={{ width: "100%", height: "100%" }}
    />
  );
};

export default PdfChart;


